import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PaymentList.css';
import { CSVLink } from 'react-csv';
import { useUser } from '../context/UserContext';
import Select from 'react-select';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const PaymentList = () => {
  const { selectedCenter, userData } = useUser();
  const [localSelectedCenter, setLocalSelectedCenter] = useState(selectedCenter);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [academyNameMap, setAcademyNameMap] = useState({});
  const [totalRevenue, setTotalRevenue] = useState("0.00");
  const token = localStorage.getItem('auth-token');

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [academyFilter, setAcademyFilter] = useState('All');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState([]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('All');

  const roleDaysLimit = {
    "centre manager": 31,
    "rotational centre manager": 31,
  };
  const defaultDays = 7;

  function getDaysLimit(userData) {
    const roles = (userData?.roles || []).map(r => r.toLowerCase());
    for (let role in roleDaysLimit) {
      if (roles.includes(role)) {
        return roleDaysLimit[role];
      }
    }
    return undefined; 
  }

  const today = new Date();
  const daysAllowed = getDaysLimit(userData);
  const pastLimit = new Date();
  pastLimit.setDate(today.getDate() - (daysAllowed || defaultDays));

  const formattedPastLimit = pastLimit.toISOString().split('T')[0];
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const formattedTomorrow = tomorrow.toISOString().split('T')[0];

  const [fromDateFilter, setFromDateFilter] = useState(formattedPastLimit);
  const [toDateFilter, setToDateFilter] = useState(formattedTomorrow);

  useEffect(() => {
    setLocalSelectedCenter(selectedCenter);
  }, [selectedCenter]);

  const abortRef = useRef(null);
  const latestReq = useRef(0);


  const refreshData = useCallback(async () => {
    const reqId = ++latestReq.current;
    setLoading(true);
    setError('');
    setPayments([]); 

    try {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      let apiUrl = process.env.REACT_APP_API_ACADEMY_PAYMENT_REPORTS;

      const academyIdsToSend =
        userData?.userType === "ACADEMY"
          ? (localSelectedCenter ? [localSelectedCenter] : userData.academyIds)
          : [];

      const bodyPayload = {
        fromDate: fromDateFilter,
        toDate: toDateFilter,
        academyIds:
          userData?.userType === "ACADEMY"
            ? academyIdsToSend
            : localSelectedCenter
              ? (Array.isArray(localSelectedCenter) ? localSelectedCenter : [localSelectedCenter])
              : null,
        ...(paymentMethodFilter.length > 0 && { paymentMethodsList: paymentMethodFilter }),
        ...(paymentStatusFilter !== 'All' && { paymentStatus: paymentStatusFilter }),
        ...(paymentTypeFilter !== 'All' && { paymentTypes: paymentTypeFilter }),
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(bodyPayload),
        signal: controller.signal,
      });

      const text = await response.text();

      if (reqId !== latestReq.current) return;

      if (!text || text.trim() === "" || text === "null") {
        setError("No data available.");
        setTotalRevenue("0.00");
        setPayments([]);
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        setError("Invalid response format from server.");
        setTotalRevenue("0.00");
        setPayments([]);
        return;
      }

      if (data.success && data.data?.payments) {
        const formattedPayments = data.data.payments.map((payment) => ({
          id: payment?.id,
          academyName: payment?.academy_name || "Unknown Academy",
          paymentType: payment?.payment_type,
          paymentMethod: payment?.payment_method,
          paidBy: payment?.paid_by_user || "-",
          amountPaid: `${payment?.amount_paid}`,
          commission: `${payment?.commission}`,
          gatewayCharges: `${payment?.payment_gateway_fee}`,
          amountTransferred: `${payment?.transferable_amount}`,
          paymentDate: new Date(payment?.created_at).toLocaleDateString('en-GB'),
          status: payment?.status,
          dateOfPaymentTransfer: payment?.transfer_on
            ? new Date(payment.transfer_on).toLocaleDateString('en-GB')
            : "-",
          referenceID: payment?.refId,
          playerName: payment?.player_name || "-",
          playerMobile: payment?.player_mobile || "-",
          parentMobile: payment?.parent_mobile || "-",
          remark: payment?.remark || "-",
          batch: payment?.batches || "-",
          term: payment?.term || "-",
          amount: payment?.amount || "-",
          sport_name: payment?.sport_name || '-'
        }));

        setPaymentMethods(data.data.paymentMethods || []);
        setPaymentStatuses(data.data.paymentStatus || []);
        setPaymentTypes(data.data.paymentTypes || []);
        setTotalRevenue(
          data.data.sumOfTransferableAmount
            ? parseFloat(data.data.sumOfTransferableAmount).toFixed(2)
            : "0.00"
        );
        setAcademyNameMap(data.data.academyNameMap || {});
        setPayments(formattedPayments);
      } else {
        setError("No data available.");
        setPayments([]);
      }
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') {
      } else {
        setError("Error fetching data: " + err.message);
        setPayments([]);
      }
    } finally {
      if (reqId === latestReq.current) setLoading(false);
    }
  }, [
    token,
    userData,
    localSelectedCenter,
    fromDateFilter,
    toDateFilter,
    paymentMethodFilter,
    paymentStatusFilter,
    paymentTypeFilter,
  ]);

  useEffect(() => {
    const t = setTimeout(() => { refreshData(); }, 300);
    return () => clearTimeout(t);
  }, [refreshData]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const headers = [
    { label: "Academy Name", key: "academyName" },
    { label: "Payment Type", key: "paymentType" },
    { label: "Payment Method", key: "paymentMethod" },
    { label: "Sport", key: "sport_name" },
    { label: "Paid By", key: "paidBy" },
    { label: "Amount Paid", key: "amountPaid" },
    { label: "Commission", key: "commission" },
    { label: "Gateway Charges", key: "gatewayCharges" },
    { label: "Amount Transferred", key: "amountTransferred" },
    { label: "Payment Date", key: "paymentDate" },
    { label: "Status", key: "status" },
    { label: "Date of Payment Transfer", key: "dateOfPaymentTransfer" },
    { label: "Reference ID", key: "referenceID" },
    { label: "Player Name", key: "playerName" },
    { label: "Player Mobile", key: "playerMobile" },
    { label: "Parent Mobile", key: "parentMobile" },
    { label: "Remark", key: "remark" },
    { label: "Batch", key: "batch" },
    { label: "Term", key: "term" },
    { label: "Amount", key: "amount" }
  ];

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);

  const academyNames = Object.values(academyNameMap);

  useEffect(() => {
    if (localSelectedCenter === null) {
      setAcademyFilter('All');
    } else if (academyNameMap[localSelectedCenter]) {
      setAcademyFilter(academyNameMap[localSelectedCenter]);
    }
  }, [localSelectedCenter, academyNameMap]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleAcademyFilterChange = (selectedOption) => {
    setAcademyFilter(selectedOption?.value || 'All');
  };

  const handlePaymentMethodFilterChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setPaymentMethodFilter(selectedValues);
  };

  const handlePaymentStatusFilterChange = (selectedOption) => {
    setPaymentStatusFilter(selectedOption?.value || 'All');
  };

  const handlePaymentTypeFilterChange = (selectedOption) => {
    setPaymentTypeFilter(selectedOption?.value || 'All');
  };

  const handleFromDateChange = (e) => { setFromDateFilter(e.target.value); };
  const handleToDateChange = (e) => { setToDateFilter(e.target.value); };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen !== null && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => { document.removeEventListener('click', handleClickOutside); };
  }, [dropdownOpen]);

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else {
        aValue = aValue?.toString().toLowerCase() || "";
        bValue = bValue?.toString().toLowerCase() || "";
      }

      if (aValue < bValue) return direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const filteredPayments = payments.filter((payment) => {
    return (
      (academyFilter === 'All' || payment.academyName === academyFilter) &&
      (paymentMethodFilter.length === 0 || paymentMethodFilter.includes(payment.paymentMethod)) &&
      (paymentStatusFilter === 'All' || payment.status === paymentStatusFilter) &&
      (paymentTypeFilter === 'All' || payment.paymentType === paymentTypeFilter) &&
      Object.values(payment).some((value) => value && value.toString().toLowerCase().includes(searchTerm))
    );
  });

  const sortedData = sortData(filteredPayments);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1); };

  const numberOfPayments = filteredPayments.length;

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">{page}</span>
      )
    );

  const totalEntries = filteredPayments.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const academyOptions = userData?.userType === 'ACADEMY'
    ? (userData.academyIds || []).map(id => ({
        value: id,
        label: academyNameMap[id] || `Academy ${id}`
      }))
    : Object.entries(academyNameMap).map(([id, name]) => ({
        value: Number(id),
        label: name,
      }));

  const allowedPaymentMethods = ['RAZORPAY', 'CASH', 'ONLINE', 'WRITE_OFF', 'UPI'];
  const paymentMethodOptions = (paymentMethods || [])
    .filter(method => allowedPaymentMethods.includes(method))
    .map(method => ({
      value: method,
      label: {
        RAZORPAY: 'Razorpay',
        CASH: 'Cash',
        ONLINE: 'Online',
        WRITE_OFF: 'Write Off',
        UPI: 'UPI'
      }[method] || method
    }));

  const allowedPaymentStatuses = ['CAPTURED', 'SETTLE'];
  const allowedPaymentTypes = ['FEES', 'PLAYING_SUBSCRIPTION'];

  const paymentStatusOptions = [
    { value: 'All', label: 'All Statuses' },
    ...(paymentStatuses || [])
      .filter(status => allowedPaymentStatuses.includes(status))
      .map(status => ({
        value: status,
        label: status === 'CAPTURED' ? 'Captured' :
               status === 'SETTLE'   ? 'Settle'   : status
      }))
  ];

  const paymentTypeOptions = [
    { value: 'All', label: 'All Types' },
    ...(paymentTypes || [])
      .filter(type => allowedPaymentTypes.includes(type))
      .map(type => ({
        value: type,
        label: type === 'FEES' ? 'Fees'
             : type === 'PLAYING_SUBSCRIPTION' ? 'Playing Subscription'
             : type === 'COURT_BOOKING' ? 'Court Booking'
             : type
      }))
  ];

  return (
    <div className="enrollment-container">
      <div className="sales-filters-login">
        <div className="floating-select">
          <Select
            options={academyOptions}
            value={academyOptions.find(option => option.value === Number(localSelectedCenter))}
            onChange={option => setLocalSelectedCenter(option ? option.value : null)}
            placeholder="Academy"
            classNamePrefix="react-select"
            className={`sales-filter-court-booking ${academyFilter ? "has-value" : ""}`}
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            isClearable
          />
          {academyFilter && <label htmlFor="academyFilter">All Academy</label>}
        </div>

        <div className="floating-select">
          <Select
            options={paymentMethodOptions}
            isMulti
            className={`sales-filter-court-booking ${paymentMethodFilter.length > 0 ? "has-value" : ""}`}
            onChange={handlePaymentMethodFilterChange}
            value={paymentMethodOptions.filter(option => paymentMethodFilter.includes(option.value))}
            placeholder="Method"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {paymentMethodFilter.length > 0 && (
            <label htmlFor="paymentMethodFilter">Payment Method</label>
          )}
        </div>

        <div className="floating-select">
          <Select
            options={paymentStatusOptions}
            className={`sales-filter-court-booking ${paymentStatusFilter ? "has-value" : ""}`}
            onChange={handlePaymentStatusFilterChange}
            placeholder="Status"
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            classNamePrefix="react-select"
            isClearable
          />
          {paymentStatusFilter && <label htmlFor="paymentStatusFilter">Payment Status</label>}
        </div>

        <div className="floating-select">
          <Select
            options={paymentTypeOptions}
            className={`sales-filter-court-booking ${paymentTypeFilter ? "has-value" : ""}`}
            onChange={handlePaymentTypeFilterChange}
            placeholder="Type"
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            classNamePrefix="react-select"
            isClearable
          />
          {paymentTypeFilter && <label htmlFor="paymentTypeFilter">Payment Type</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${fromDateFilter ? "has-value" : ""}`}
            value={fromDateFilter}
            onChange={e => setFromDateFilter(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            min={daysAllowed ? formattedPastLimit : ""}
            max={formattedTomorrow}
          />
          {fromDateFilter && <label htmlFor="fromDateFilter">From Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${toDateFilter ? "has-value" : ""}`}
            value={toDateFilter}
            onChange={e => setToDateFilter(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            min={fromDateFilter}
            max={formattedTomorrow}
          />
          {toDateFilter && <label htmlFor="toDateFilter">To Date</label>}
        </div>
      </div>

      <div className="ps-cards-9k3">
        <div className="ps-card-9k3">
          <div className="ps-card-label-9k3">Total Revenue</div>
          <div className="ps-card-value-9k3">₹{totalRevenue}</div>
        </div>
        <div className="ps-card-9k3">
          <div className="ps-card-label-9k3">Number of Payments</div>
          <div className="ps-card-value-9k3">{numberOfPayments}</div>
        </div>
      </div>

      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearch}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>

        <CSVLink
          data={filteredPayments.map(({ playerMobile, parentMobile, ...rest }) => rest)}
          headers={headers.filter(h => h.key !== 'playerMobile' && h.key !== 'parentMobile')}
          filename="PaymentList.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button">
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="tt-action-btn" onClick={refreshData} disabled={loading}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track"><div className="progress-bar"></div></div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredPayments.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      {!loading && !error && filteredPayments.length > 0 && (
        <>
          <div className="tt-table-container">
            <table className="tt-table">
              <thead>
                <tr>
                  {headers.map(({ key, label }) => (
                    <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                      <div className="th-content">
                        <span>{label}</span>
                        <FiChevronDown
                          className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentRows.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.academyName}</td>
                    <td>{payment.paymentType}</td>
                    <td>{payment.paymentMethod}</td>
                    <td>{payment.sport_name}</td>
                    <td>{payment.paidBy}</td>
                    <td>{payment.amountPaid}</td>
                    <td>{payment.commission}</td>
                    <td>{payment.gatewayCharges}</td>
                    <td>{payment.amountTransferred}</td>
                    <td>{payment.paymentDate}</td>
                    <td>{payment.status}</td>
                    <td>{payment.dateOfPaymentTransfer}</td>
                    <td>{payment.referenceID}</td>
                    <td>{payment.playerName}</td>
                    <td>{payment.playerMobile}</td>
                    <td>{payment.parentMobile}</td>
                    <td>{payment.remark}</td>
                    <td>{payment.batch}</td>
                    <td>{payment.term}</td>
                    <td>{payment.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="showing-entries">
              Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
            </div>
            <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>
              Previous
            </button>
            {renderPagination()}
            <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentList;
